import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { ControlStatusJP } from '../../../../../shared/consts';
import { DynamoDbService } from '../../services/dynamodb.service';
import { FilterService } from '../../services/filter.service';
import { WebSocketService } from '../../services/websocket.service';
import { ControlData, ControlDetail } from '../../shared/app-consts';
import { ChangeStatusComponent } from '../change-status/change-status.component';

export interface AccountData {
  AccountID: string,
  TeamName: string,
  TotalScore: number
}
interface LatestApplication {
  ControlID: string,
  ApplicationTimestamp: string
}

@Component({
  selector: 'app-tables',
  templateUrl: './tables.component.html',
  styleUrl: './tables.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  #ddb = inject(DynamoDbService);
  #cdr = inject(ChangeDetectorRef);
  #webSocket = inject(WebSocketService);
  #filter = inject(FilterService);
  #dialog = inject(MatDialog);

  accountData: AccountData[] = [];
  controlStatus: { [acountID: string]: ControlData[] } = {};
  filteredDataSource: { [accountID: string]: MatTableDataSource<ControlData> } = {};
  ControlStatusJP = ControlStatusJP;
  searchText: string = '';
  latestApplications: { [AccountID: string]: LatestApplication } = {};
  uniqueServices: string[] = [];
  selectedServices: { [service: string]: boolean } = {};
  selectedStatuses: { [status: string]: boolean } = {
    '申請済み': false,
    '未完了': false,
    '完了': false
  };
  showFilter: boolean = false;
  isFiltering: boolean = false;

  onSearchTextChanged(searchText: string): void {
    console.log('Search text changed:', searchText);
    this.searchText = searchText;
    this.filterData();
  }
  onSelectedServicesChanged(selectedServices: { [service: string]: boolean }): void {
    this.selectedServices = selectedServices;
    this.filterData();
  }
  onSelectedStatusesChanged(selectedStatuses: { [status: string]: boolean }): void {
    this.selectedStatuses = selectedStatuses;
    this.filterData();
  }

  constructor() {
    this.#webSocket.controlUpdate.subscribe(({ accountId, controlId, status }) => {
      console.log('Received control update:', accountId, controlId, status);
      this.#ddb.fetchControlDetailByAccountID(accountId).subscribe(details => {
        this.#fetchControlDetail(details, accountId);
      });
    });
  }

  ngOnInit(): void {
    this.#webSocket.currentAccountID = '';
    this.#ddb.fetchAccountTable().subscribe(data => {
      if (data.Items) {
        this.accountData = data.Items
          .map(item => ({
            AccountID: item['AccountID'].S,
            TeamName: item['TeamName']?.S,
            TotalScore: item['TotalScore']?.N ? Number(item['TotalScore'].N) : 0
          }))
          .filter((item): item is AccountData => item !== undefined);
        for (const account of this.accountData) {
          this.#ddb.fetchControlDetailByAccountID(account.AccountID)
            .subscribe(details => {
              this.#fetchControlDetail(details, account.AccountID);
              this.#applyPaginator();
            }
            );
        }
      }
    }
    );
  }
  #fetchControlDetail(details: ControlDetail[], accountId: string): void {
    const controlDetailsForTables = this.#ddb.convertControlData(details);
    const updateStatus: ControlData[] = [];
    if (!this.controlStatus[accountId]) {
      this.controlStatus[accountId] = [];
    }
    for (const detail of controlDetailsForTables) {
      updateStatus.push({
        ControlID: detail.ControlID,
        Status: detail.Status,
        ApplicationTimestamp: detail.ApplicationTimestamp,
        ApplicationCount: detail.ApplicationCount
      });
      this.#updateLatestApplication(accountId, detail);
    }
    this.controlStatus[accountId] = updateStatus;
    this.filteredDataSource[accountId] = new MatTableDataSource<ControlData>([...updateStatus]);
    this.#initializeUniqueServicesAndStatuses();
    this.filterData();
  }

  #updateLatestApplication(accountId: string, detail: ControlData): void {
    if (detail.ApplicationTimestamp) {
      if (!this.latestApplications[accountId] || this.latestApplications[accountId].ApplicationTimestamp < detail.ApplicationTimestamp) {
        this.latestApplications[accountId] = {
          ControlID: detail.ControlID,
          ApplicationTimestamp: detail.ApplicationTimestamp
        };
      }
    }
  }

  #initializeUniqueServicesAndStatuses(): void {
    if (this.uniqueServices.length > 0) {
      return;
    }
    const firstAccountId = Object.keys(this.controlStatus)[0];
    const allServices = this.controlStatus[firstAccountId].map(item =>
      this.#getServiceName(item.ControlID));
    this.uniqueServices = Array.from(new Set(allServices));
    for (const service of this.uniqueServices) {
      this.selectedServices[service] = false;
    }
  }
  #getServiceName(controlId: string): string {
    return controlId.split('.')[0];
  }

  filterData(): void {
    this.filteredDataSource = {};
    if (this.searchText || Object.values(this.selectedServices).some(service => service) || Object.values(this.selectedStatuses).some(status => status)) {
      this.isFiltering = true;
    } else {
      this.isFiltering = false;
    }
    for (const accountId in this.controlStatus) {
      let filteredResults = this.controlStatus[accountId];
      this.filteredDataSource[accountId] = new MatTableDataSource<ControlData>(filteredResults);

      if (this.searchText) {
        filteredResults = this.#filter.filterBySearchText(filteredResults, this.searchText);
      }
      filteredResults = this.#filter.filterServicesAndStatuses(filteredResults, this.selectedServices, this.selectedStatuses);
      this.filteredDataSource[accountId] = new MatTableDataSource<ControlData>(filteredResults);
    }
    if (this.filteredDataSource) {
      setTimeout(() => {
        this.#applyPaginator();
      });
    }
    this.#cdr.markForCheck();
  }
  #applyPaginator(): void {
    Object.values(this.filteredDataSource).forEach(dataSource => {
      dataSource.paginator = this.paginator;
    });
    this.#cdr.markForCheck();
  }
  getRankByTotalScore(totalScore: number): number {
    const sortedScores = this.accountData.map(account => account.TotalScore).sort((a, b) => b - a);
    return sortedScores.indexOf(totalScore) + 1;
  }

  openAdminControlsDialog(): void {
    this.#dialog.open(ChangeStatusComponent, {
      data: {
        accountData: this.accountData,
        controlStatus: this.controlStatus,
        latestApplications: this.latestApplications
      }
    });
    // dialogRef.afterClosed().subscribe(result => {
    //   console.log('The dialog was closed:', result);
    // });
  }

  getCompletionPercentage(accountId: string): number {
    const accountControls = this.filteredDataSource[accountId];
    if (!accountControls || accountControls.data.length === 0) {
      return 0;
    }
    const completedControls = accountControls.data.filter(control => control.Status === 4).length;
    return Math.round((completedControls / accountControls.data.length) * 100);
  }

  getApplicationPercentage(accountId: string): number {
    const accountControls = this.filteredDataSource[accountId];
    if (!accountControls || accountControls.data.length === 0) {
      return 0;
    }
    const appliedControls = accountControls.data.filter(control => control.Status === 1 || control.Status === 2 || control.Status === 4).length;
    return Math.round((appliedControls / accountControls.data.length) * 100);
  }

  getCompletedControlCount(accountId: string): number {
    const accountControls = this.filteredDataSource[accountId];
    if (!accountControls || accountControls.data.length === 0) {
      return 0;
    }
    return accountControls.data.filter(control => control.Status === 4).length;
  }
  
  getTotalControlCount(accountId: string): number {
    const accountControls = this.filteredDataSource[accountId];
    return accountControls ? accountControls.data.length : 0;
  }
}
